// ===== 대운(大運) 계산 엔진 =====
// 대운: 10년 주기로 바뀌는 사주의 큰 운세 흐름

import {
  HEAVENLY_STEMS, HEAVENLY_STEMS_KOR,
  EARTHLY_BRANCHES, EARTHLY_BRANCHES_KOR,
  STEM_ELEMENTS, BRANCH_ELEMENTS,
  type Element, type SajuResult,
  getJulianDay, getTermDateJD,
} from './sajuCalculations';

// ===== 60갑자 인덱스 =====
function ganzhiIdx(stemIdx: number, branchIdx: number): number {
  for (let i = 0; i < 60; i++) {
    if (i % 10 === stemIdx && i % 12 === branchIdx) return i;
  }
  return 0;
}

// ===== 대운 방향 결정 =====
// 양년(甲丙戊庚壬 = 천간 인덱스 짝수) + 남자 OR 음년 + 여자 → 순행
// 음년(乙丁己辛癸 = 천간 인덱스 홀수) + 남자 OR 양년 + 여자 → 역행
export function getDaeunDirection(yearStemIdx: number, gender: 'male' | 'female'): 'forward' | 'backward' {
  const isYangYear = yearStemIdx % 2 === 0; // 짝수 천간 = 양년
  if ((isYangYear && gender === 'male') || (!isYangYear && gender === 'female')) {
    return 'forward';
  }
  return 'backward';
}

// ===== 출생일 기준 앞/뒤 절기까지의 일수 계산 =====
function findTermDaysDiff(
  year: number, month: number, day: number, hour: number,
  direction: 'forward' | 'backward',
): number {
  const birthJD = getJulianDay(year, month, day) + hour / 24;
  const currTermIdx = month - 1; // 0-based, 소한=0
  const currTermJD = getTermDateJD(year, currTermIdx);

  let targetJD: number;

  if (direction === 'forward') {
    // 다음 절기
    if (birthJD < currTermJD) {
      // 이번 달 절기가 아직 안 지남
      targetJD = currTermJD;
    } else {
      // 이번 달 절기는 지났으므로 다음 달 절기
      const nextIdx = month === 12 ? 0 : month; // 0-based
      const nextYear = month === 12 ? year + 1 : year;
      targetJD = getTermDateJD(nextYear, nextIdx);
    }
    return targetJD - birthJD;
  } else {
    // 이전 절기
    if (birthJD < currTermJD) {
      // 이번 달 절기 전 → 이전 달 절기
      const prevIdx = month === 1 ? 11 : month - 2; // 0-based
      const prevYear = month === 1 ? year - 1 : year;
      targetJD = getTermDateJD(prevYear, prevIdx);
    } else {
      // 이번 달 절기 이후 → 이번 달 절기
      targetJD = currTermJD;
    }
    return birthJD - targetJD;
  }
}

// ===== 대운 시작 나이 계산 =====
// 3일 = 1년, 1일 = 4개월
export interface DaeunStartAge {
  years: number;
  months: number;
}

function calcStartAge(daysDiff: number): DaeunStartAge {
  const totalDays = Math.max(0, daysDiff);
  const years  = Math.floor(totalDays / 3);
  const months = Math.round((totalDays % 3) * 4);
  // 개월 수가 12 이상이면 년으로 넘김
  if (months >= 12) return { years: years + 1, months: months - 12 };
  return { years, months };
}

// ===== 대운 엔트리 =====
export interface DaeunEntry {
  index:        number;   // 0-based 순서
  startAge:     number;   // 대운 시작 나이
  endAge:       number;   // 대운 종료 나이 (startAge + 9)
  stemIdx:      number;
  branchIdx:    number;
  stem:         string;
  branch:       string;
  stemKor:      string;
  branchKor:    string;
  stemElement:  Element;
  branchElement: Element;
  isCurrent:    boolean;  // 현재 대운 여부
  startYear:    number;   // 대운 시작 그레고리력 연도
}

export interface DaeunResult {
  direction:     'forward' | 'backward';
  startAge:      DaeunStartAge;  // 첫 대운 시작 나이
  entries:       DaeunEntry[];
  currentIndex:  number;         // 현재 대운 인덱스 (-1 이면 대운 시작 전)
}

// ===== 전체 대운 계산 =====
export function calculateDaeun(
  sajuResult: SajuResult,
  birthYear:  number,
  birthMonth: number,
  birthDay:   number,
  correctedHour: number,
  gender: 'male' | 'female',
): DaeunResult {
  const { yearPillar, monthPillar } = sajuResult;
  const direction = getDaeunDirection(yearPillar.stemIdx, gender);

  // 절기까지 날수 → 대운 시작 나이
  const daysDiff = findTermDaysDiff(birthYear, birthMonth, birthDay, correctedHour, direction);
  const startAge = calcStartAge(daysDiff);

  // 月柱의 60갑자 인덱스
  const monthGanzhiIdx = ganzhiIdx(monthPillar.stemIdx, monthPillar.branchIdx);

  // 현재 나이 계산 (2026년 기준)
  const currentYear = new Date().getFullYear();
  const currentAge  = currentYear - birthYear;

  // 대운 10개 생성 (최대 100세까지)
  const entries: DaeunEntry[] = [];
  for (let i = 0; i < 10; i++) {
    const offset   = direction === 'forward' ? i + 1 : -(i + 1);
    const gIdx     = ((monthGanzhiIdx + offset) % 60 + 60) % 60;
    const si       = gIdx % 10;
    const bi       = gIdx % 12;
    const ageStart = startAge.years + i * 10;
    const ageEnd   = ageStart + 9;

    entries.push({
      index:        i,
      startAge:     ageStart,
      endAge:       ageEnd,
      stemIdx:      si,
      branchIdx:    bi,
      stem:         HEAVENLY_STEMS[si],
      branch:       EARTHLY_BRANCHES[bi],
      stemKor:      HEAVENLY_STEMS_KOR[si],
      branchKor:    EARTHLY_BRANCHES_KOR[bi],
      stemElement:  STEM_ELEMENTS[si],
      branchElement: BRANCH_ELEMENTS[bi],
      isCurrent:    currentAge >= ageStart && currentAge <= ageEnd,
      startYear:    birthYear + ageStart,
    });
  }

  const currentIndex = entries.findIndex(e => e.isCurrent);

  return { direction, startAge, entries, currentIndex };
}
